import React from 'react';
import { NavLink } from 'react-router-dom';
import {
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  CircularProgress,
  Box
} from '@mui/material';
import { usePermissions } from '../../context/PermissionContext';

// Material UI Icons
import DashboardIcon from '@mui/icons-material/Dashboard';
import SettingsIcon from '@mui/icons-material/Settings';
import PeopleIcon from '@mui/icons-material/People';
import StoreIcon from '@mui/icons-material/Store';
import CurrencyExchangeIcon from '@mui/icons-material/CurrencyExchange';
import SupportIcon from '@mui/icons-material/Support';
import BarChartIcon from '@mui/icons-material/BarChart';
import AdUnitsIcon from '@mui/icons-material/AdUnits';
import PersonIcon from '@mui/icons-material/Person';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import NotificationsIcon from '@mui/icons-material/Notifications';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import HomeRepairServiceIcon from '@mui/icons-material/HomeRepairService';
import HandshakeIcon from '@mui/icons-material/Handshake';
import LanguageIcon from '@mui/icons-material/Language';
import SavingsIcon from '@mui/icons-material/Savings';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import EmailIcon from '@mui/icons-material/Email';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';

// Sidebar items: shown when the user holds any of the page keys in `perms` (empty = everyone)
const sidebarItems = [
  {
    name: 'Dashboard',
    icon: <DashboardIcon />,
    path: '/dashboard',
    perms: ['dashboard.overview'],
  },
  {
    name: 'Message',
    icon: <EmailIcon />,
    path: '/messages',
    perms: ['messages.inbox'],
  },
  {
    name: 'Master',
    icon: <SettingsIcon />,
    path: '/master',
    perms: ['master.pincodeCreate', 'master.pincodeRemove', 'master.designations', 'master.designationPrivileges', 'master.gst', 'master.commissionPercent', 'master.cashbackSequence', 'master.adPrice'],
  },
  {
    name: 'User Creation',
    icon: <PeopleIcon />,
    path: '/user-creation',
    perms: ['users.admins', 'users.pincodes', 'users.designations'],
  },
  {
    name: 'Merchants Module',
    icon: <StoreIcon />,
    path: '/vendor-registration',
    perms: ['merchants.allShops', 'merchants.commission', 'merchants.rejected', 'merchants.pending', 'merchants.advanceFund'],
  },
  {
    name: 'Q-PAY Centre Module',
    icon: <CurrencyExchangeIcon />,
    path: '/msb',
    perms: ['qpayCentre.all', 'qpayCentre.daily', 'qpayCentre.transactions', 'qpayCentre.settlementsHandle', 'qpayCentre.settlementsReport'],
  },
  {
    name: 'Customer Help-Desk',
    icon: <SupportIcon />,
    path: '/support',
    perms: ['support.customer', 'support.ad'],
  },
  {
    name: 'Advertisement Module',
    icon: <AdUnitsIcon />,
    path: '/advertisement',
    perms: ['ads.providers', 'ads.fullpage', 'ads.banner', 'ads.live', 'ads.nonLive', 'ads.requests', 'ads.calculator'],
  },
  {
    name: 'Customer Module',
    icon: <PersonIcon />,
    path: '/customer-accounts',
    perms: ['customers.synopsis', 'customers.all', 'customers.logins', 'customers.deletion', 'customers.cashQr'],
  },
  {
    name: 'State Head Module',
    icon: <BarChartIcon />,
    path: '/state-admin-report/',
    perms: ['stateHead.all', 'stateHead.daily', 'stateHead.transactions', 'stateHead.settlementsHandle', 'stateHead.settlementsReport'],
  },
  // {
  //   name: 'Bank',
  //   icon: <AccountBalanceIcon />,
  //   path: '/bank-management',
  //   modules: [7100, 7101, 7102],
  // },
  {
    name: 'Notifications',
    icon: <NotificationsIcon />,
    path: '/notification-management',
    perms: ['notifications.customer', 'notifications.shop', 'notifications.agent'],
  },
  {
    name: 'Smart Point Module',
    icon: <AccountBalanceWalletIcon />,
    path: '/smart-point-module',
    perms: ['smartPoint.points', 'smartPoint.welfare', 'smartPoint.withdrawals'],
  },
  {
    name: 'On-Demand Module',
    icon: <HomeRepairServiceIcon />,
    path: '/on-demand-module',
    perms: ['onDemand.requests', 'onDemand.helpDesk'],
  },
  {
    name: 'Online Service Module',
    icon: <LanguageIcon />,
    path: '/online-service-module',
    perms: ['onlineService.requests'],
  },
  {
    name: 'Finance Service Module',
    icon: <SavingsIcon />,
    path: '/finance-service-module',
    perms: ['financeService.requests'],
  },
  {
    name: 'Insurance Module',
    icon: <HealthAndSafetyIcon />,
    path: '/insurance-module',
    perms: ['insuranceService.requests'],
  },
  {
    name: 'Partnership Module',
    icon: <HandshakeIcon />,
    path: '/partnership-module',
    perms: ['partnership.joinUs'],
  },
  {
    name: 'Super Admin Module',
    icon: <AdminPanelSettingsIcon />,
    path: '/admin-report',
    perms: ['reports.adminTransactions', 'reports.adminDaily', 'reports.pool', 'reports.cash'],
  },
  {
    name: 'Non-Customer Commission',
    icon: <ReceiptLongIcon />,
    path: '/admin-report/non-customer-commission',
    perms: ['reports.nonCustomerCommission'],
  },
  {
    name: 'Company Income',
    icon: <AccountBalanceWalletIcon />,
    path: '/ho-funds',
    perms: ['hoFunds.overview'],
  },
  {
    name: 'Profile',
    icon: <PersonIcon />,
    path: '/profile',
    perms: [],
  },
];

const Sidebar = () => {
  const { loading, canAny } = usePermissions();

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  const visibleItems = sidebarItems.filter((item) => !item.perms || item.perms.length === 0 || canAny(item.perms));

  return (
    <List>
      {visibleItems.map((item) => (
        <ListItem
          key={item.name}
          button
          component={NavLink}
          to={item.path}
          sx={{
            py: 1.2,
            px: 2.5,
            cursor: 'pointer',
            userSelect: 'none',
            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
            borderLeft: '4px solid transparent',
            color: '#475569',
            '& .MuiListItemIcon-root': {
              color: '#64748b',
              minWidth: 42,
              transition: 'color 0.2s ease, transform 0.2s ease',
            },
            '& .MuiListItemText-primary': {
              fontWeight: 600,
              fontSize: '0.9rem',
              transition: 'color 0.2s ease',
            },
            '&.active': {
              backgroundColor: 'primary.main',
              color: '#ffffff',
              borderLeftColor: '#cc8400',
              boxShadow: '0 4px 14px rgba(255, 165, 0, 0.35)',
              '& .MuiListItemIcon-root': {
                color: '#ffffff',
              },
              '& .MuiListItemText-primary': {
                fontWeight: 700,
                color: '#ffffff',
              },
            },
            '&:hover:not(.active)': {
              backgroundColor: 'rgba(255, 165, 0, 0.12)',
              color: 'primary.dark',
              borderLeftColor: 'primary.main',
              transform: 'translateX(3px)',
              '& .MuiListItemIcon-root': {
                color: 'primary.main',
                transform: 'scale(1.1)',
              },
              '& .MuiListItemText-primary': {
                color: 'primary.dark',
              },
            },
          }}
        >
          <ListItemIcon>{item.icon}</ListItemIcon>
          <ListItemText primary={item.name} />
        </ListItem>
      ))}
    </List>
  );
};

export default Sidebar;